import { useState } from 'react';
import { createPortal } from 'react-dom';
import { BRAND_ICON_SRC, BRAND_LINE_JA, BRAND_LOGO } from '../brand';
import { useStore } from '../state/store';
import { HOME_FEATURED, HOME_NEWS, HOME_TRENDING, type HomeNewsItem } from './homeContent';

export function HomeScreen() {
  const setView = useStore((s) => s.setView);
  const conflictCount = useStore((s) => s.conflictCount);
  const roms = useStore((s) => s.roms);
  const [newsItem, setNewsItem] = useState<HomeNewsItem | null>(null);

  return (
    <div className="library-shell flex h-full flex-col md:h-auto landscape:h-auto">
      {/* スマホ縦用ヘッダー（PC/横は AppShell 上部ナビ） */}
      <header className="pt-safe-header shrink-0 border-b border-base-600/60 bg-[#07090f] px-4 pb-3 md:hidden landscape:hidden">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <img
              src={BRAND_ICON_SRC}
              alt=""
              width={36}
              height={36}
              className="h-9 w-9 shrink-0 rounded-lg"
              draggable={false}
            />
            <div className="min-w-0">
              <h1 className="brand-mark text-xl font-semibold tracking-tight text-white" aria-label={BRAND_LOGO}>
                EmYu<span className="text-accent">-Ba</span>
              </h1>
              <p className="truncate text-[0.7rem] text-slate-500">{BRAND_LINE_JA}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              className="btn-ghost relative !min-h-0 !rounded-lg !px-2.5 !py-1.5 !text-xs"
              onClick={() => setView('my')}
              aria-label="アカウント"
            >
              My
              {conflictCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[0.6rem] font-bold text-white">
                  {conflictCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto min-h-0 w-full max-w-6xl flex-1 overflow-y-auto px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] pt-4 md:overflow-visible md:pb-10 landscape:overflow-visible landscape:pb-10">
        {/* 1. Featured */}
        <section aria-labelledby="home-featured">
          <p id="home-featured" className="field-label !mb-2">
            Featured
          </p>
          <button
            type="button"
            onClick={() => setView('games')}
            className="card group relative w-full overflow-hidden p-5 text-left transition-colors hover:border-accent/40 sm:p-7"
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                background:
                  'radial-gradient(ellipse at 80% 20%, rgba(74,222,128,0.18), transparent 55%), linear-gradient(135deg, #0d1119, #151b26 60%, #07090f)',
              }}
              aria-hidden
            />
            <div className="relative">
              <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-accent">
                {HOME_FEATURED.badge}
              </p>
              <h2 className="brand-mark mt-2 text-2xl font-semibold text-white sm:text-3xl">{HOME_FEATURED.title}</h2>
              <p className="mt-1 font-mono text-[0.7rem] tracking-wide text-slate-500">{HOME_FEATURED.date}</p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400">{HOME_FEATURED.blurb}</p>
              <span className="btn-ghost mt-5 inline-flex !min-h-0 !px-3 !py-1.5 !text-xs group-hover:border-accent/50">
                詳細を見る
              </span>
            </div>
          </button>
        </section>

        {/* 2. Trending */}
        <section className="mt-8" aria-labelledby="home-trending">
          <div className="mb-2 flex items-end justify-between gap-2">
            <p id="home-trending" className="field-label !mb-0">
              Trending
            </p>
            <p className="text-[0.65rem] text-slate-600">プレビュー表示</p>
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {HOME_TRENDING.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setView('games')}
                className="card flex aspect-[4/3] flex-col justify-end p-3 text-left transition-colors hover:border-accent/35"
              >
                <span className="text-[0.6rem] uppercase tracking-wider text-slate-500">{g.tag}</span>
                <span className="mt-1 text-sm font-medium text-slate-100">{g.title}</span>
              </button>
            ))}
          </div>
        </section>

        {/* 3. EmYu-Ba Games */}
        <section className="mt-8" aria-labelledby="home-games">
          <p id="home-games" className="field-label !mb-2">
            EmYu-Ba Games
          </p>
          <button
            type="button"
            onClick={() => setView('games')}
            className="card w-full p-5 text-left transition-colors hover:border-accent/40"
          >
            <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-accent">
              Now Playing
            </p>
            <h3 className="mt-2 text-base font-semibold text-white">AI Novel ほか</h3>
            <p className="mt-1.5 text-sm text-slate-400">オリジナルゲームをここから遊べます。第一弾は AI Novel。</p>
            <span className="btn-ghost mt-4 inline-flex !min-h-0 !px-3 !py-1.5 !text-xs">開く</span>
          </button>
        </section>

        {/* Retro shortcut */}
        <section className="mt-8" aria-labelledby="home-retro">
          <p id="home-retro" className="field-label !mb-2">
            Retro
          </p>
          <button
            type="button"
            onClick={() => setView('library')}
            className="card flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:border-accent/40"
          >
            <div>
              <h3 className="text-base font-semibold text-white">エミュレーター</h3>
              <p className="mt-1 text-sm text-slate-400">
                {roms.length > 0 ? `ライブラリ ${roms.length} 本` : 'ROM を追加してプレイ'}
              </p>
            </div>
            <span className="btn-primary !min-h-0 shrink-0 !px-3 !py-1.5 !text-xs">開く</span>
          </button>
        </section>

        {/* 4. News */}
        <section className="mt-8 mb-2" aria-labelledby="home-news">
          <p id="home-news" className="field-label !mb-2">
            News
          </p>
          <ul className="card divide-y divide-base-600/80 overflow-hidden">
            {HOME_NEWS.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => setNewsItem(n)}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-base-700/50"
                >
                  <span className="shrink-0 font-mono text-[0.65rem] text-slate-500">{n.date}</span>
                  <span className="text-sm text-slate-200">{n.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {newsItem && <NewsDetailModal item={newsItem} onClose={() => setNewsItem(null)} />}
    </div>
  );
}

function NewsDetailModal({ item, onClose }: { item: HomeNewsItem; onClose: () => void }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] pt-[max(1rem,env(safe-area-inset-top,0px))] backdrop-blur-sm sm:p-4 md:p-4 landscape:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-news-title"
        className="flex max-h-[min(70dvh,calc(var(--app-height,100dvh)-7rem))] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-base-600 bg-base-900 shadow-xl sm:max-h-[min(88dvh,720px)] landscape:max-h-[min(88dvh,720px)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 border-b border-base-700 px-4 py-3">
          <p className="font-mono text-[0.65rem] text-slate-500">{item.date}</p>
          <h2 id="home-news-title" className="mt-1 text-sm font-semibold text-slate-100">
            {item.title}
          </h2>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300">{item.body}</p>
        </div>
        <footer className="shrink-0 border-t border-base-700 p-4">
          <button type="button" className="btn-primary w-full" onClick={onClose}>
            閉じる
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
